import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../api/client';
import './home.css';

interface JobSubmitResponse {
   job_id: string;
   job_page_url: string;
   num_queries: number;
   message: string;
}

interface FormOptions {
   xrays_only: boolean;
   exclude_nmr: boolean;
   water_as_ligand: boolean;
   lig_free_sites: boolean;
   nonstd_rsds_as_lig: boolean;
   d_aa_as_lig: boolean;
   save_apo: boolean;
   save_holo: boolean;
   save_unobserved: boolean;
   bndgrsds_threshold: string;
   res_threshold: string;
   lig_scan_radius: string;
   calc_pocket_volume: boolean;
   include_alphafold: boolean;
}

const defaultQuery = '3CQV A HEM';

const SearchForApoHoloPairs = (): React.JSX.Element => {
   const navigate = useNavigate();
   const [queries, setQueries] = useState<string>(defaultQuery);
   const [queryRows, setQueryRows] = useState<number>(3);
   const [jobName, setJobName] = useState<string>('');
   const [options, setOptions] = useState<FormOptions>({
      xrays_only: false,
      exclude_nmr: false,
      water_as_ligand: false,
      lig_free_sites: true,
      nonstd_rsds_as_lig: false,
      d_aa_as_lig: false,
      save_apo: true,
      save_holo: true,
      save_unobserved: true,
      bndgrsds_threshold: '30.0',
      res_threshold: '4.0',
      lig_scan_radius: '4.5',
      calc_pocket_volume: false,
      include_alphafold: true,
   });

   const limitRows = (rows: string[]): number => {
      const min = 3;
      const max = 9;
      return Math.max(min, Math.min(rows.length, max));
   };

   const loadExample1 = (event: React.MouseEvent<HTMLAnchorElement>): void => {
      event.preventDefault();

      const lines: string[] = ['2v57', 'A', 'prl'];
      setQueries(lines.join('\n'));
      setQueryRows(limitRows(lines));
      setJobName('SS changes in transcriptional regulator LfrR in complex with proflavine');
      // setReverseSearch(false);
   };

   const loadExample2 = (event: React.MouseEvent<HTMLAnchorElement>): void => {
      event.preventDefault();

      const lines: string[] = ['1DB1 # vitamin D3 study', '4est # porcine pancreatic elastase'];
      setQueries(lines.join('\n'));
      setQueryRows(limitRows(lines));
      setJobName('enzymes');
   };

   const loadExample3 = (event: React.MouseEvent<HTMLAnchorElement>): void => {
      event.preventDefault();

      const lines: string[] = [
         '1a73 A,B ZN   # consider ZN ligands in chains A and B of 1a73',
         '1a73 ALL ZN   # consider ZN ligands in all chains of 1a73',
         '1a73          # find and consider all ligands in all chains of 1a73',
         '1a73 A        # find and consider all ligands in chain A of 1a73',
         '1a73 A ZN,MG  # consider ZN and MG ligands in chain A of 1a73',
         '3fav ALL ZN   # consider ZN ligands in all chains of 3fav',
         '1DB1          # vitamin D3 study',
         '4est          # porcine pancreatic elastase',
         '3CQV          # reverb beta - all chains, all ligands',
         '3CQV A        # reverb beta - chain A (in this case same effect)',
         '3CQV A HEM    # reverb beta - ligand HEM (in this case same effect)',
      ];
      setQueries(lines.join('\n'));
      setQueryRows(limitRows(lines));
      setJobName('Process a small dataset');
      // setReverseSearch(false);
   };

   const loadExample4 = (event: React.MouseEvent<HTMLAnchorElement>): void => {
      event.preventDefault();

      const lines: string[] = [defaultQuery];
      setQueries(lines.join('\n'));
      setQueryRows(limitRows(lines));
      setJobName('Example 4');
   };

   const [submitError, setSubmitError] = useState<string | null>(null);

   const submitForm = (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      setSubmitError(null);

      const formData = {
         queries,
         job_name: jobName,
         email: '', // Email field is currently not implemented in the form, but can be added in the future if needed
         options,
      };

      void (async (): Promise<void> => {
         try {
            await apiFetch<JobSubmitResponse>('/api/job', {
               method: 'POST',
               body: JSON.stringify(formData),
               headers: {
                  'Content-Type': 'application/json',
               },
            });

            void navigate('/my-jobs');
         } catch (error) {
            console.error('Error submitting job:', error);
            setSubmitError(error instanceof Error ? error.message : 'Unexpected error. Is the backend running?');
         }
      })();
   };

   return (
      <div className="submit-form-box">
         <div className="form-header">
            <h1>Search for Apo-Holo Pairs</h1>
         </div>

         <form onSubmit={submitForm}>
            <div className="form-group">
               <label
                  htmlFor="queries"
                  className="thick"
                  title={"List of queries separated by newline. Everything after '#' is a comment."}
               >
                  Query / Queries
               </label>

               <div className="examples">
                  <span>
                     <a href="#" onClick={loadExample1}>
                        example 1
                     </a>
                  </span>
                  <span>
                     <a href="#" onClick={loadExample2}>
                        example 2
                     </a>
                  </span>
                  <span>
                     <a href="#" onClick={loadExample3}>
                        example 3
                     </a>
                  </span>
                  <span>
                     <a href="#" onClick={loadExample4}>
                        example 4
                     </a>
                  </span>
               </div>
               <textarea
                  className="form-control"
                  name="queries"
                  id="queries"
                  value={queries}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>): void => setQueries(e.target.value)}
                  rows={queryRows}
               ></textarea>

               <div className="name-email">
                  <div className="form-group xname">
                     <label
                        htmlFor="job_name"
                        className="thick"
                        title="Optional job that can help to identify the job later."
                     >
                        Job Name (optional)
                     </label>
                     <input
                        type="text"
                        name="job_name"
                        id="job_name"
                        className="form-control"
                        value={jobName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setJobName(e.target.value)}
                     />
                  </div>
               </div>

               <div className="options-outer">
                  <h4>Options</h4>
                  <div className="options">
                     <div className="options-left">
                        <div className="form-check">
                           <input type="checkbox" className="form-check-input" id="xray_only" />
                           <label
                              className="form-check-label"
                              htmlFor="xray_only"
                              title="Only X-ray structures will be considered during search."
                           >
                              X-ray structures only
                           </label>
                        </div>

                        <div className="form-check">
                           <input type="checkbox" className="form-check-input" id="exclude_nmr" />
                           <label
                              className="form-check-label"
                              htmlFor="exclude_nmr"
                              title="Exclude NMR structures from search results."
                           >
                              Exclude NMR structures
                           </label>
                        </div>

                        <div className="form-check">
                           <input
                              type="checkbox"
                              className="form-check-input"
                              defaultChecked={true}
                              id="lig_free_sites"
                           />
                           <label
                              className="form-check-label"
                              htmlFor="lig_free_sites"
                              title="0/1: when on resulting apo sites will be free of any other known ligands in addition to specified ligands"
                           >
                              Ligand-free sites
                           </label>
                        </div>

                        <div className="form-check">
                           <input type="checkbox" className="form-check-input" id="water_as_ligand" />
                           <label
                              className="form-check-label"
                              htmlFor="water_as_ligand"
                              title="consider HOH atoms as ligands (can be used in combination with lig_free_sites), strict"
                           >
                              Consider water as ligand
                           </label>
                        </div>

                        <div className="form-check">
                           <input type="checkbox" className="form-check-input" id="nonstd_rsds_as_lig" />
                           <label className="form-check-label" htmlFor="nonstd_rsds_as_lig" title="">
                              Consider non-standard residues as ligands
                           </label>
                        </div>

                        <div className="form-check">
                           <input type="checkbox" className="form-check-input" id="d_aa_as_lig" />
                           <label className="form-check-label" htmlFor="d_aa_as_lig" title="">
                              Consider D-amino acids as ligands
                           </label>
                        </div>

                        <div className="form-check">
                           <input type="checkbox" className="form-check-input" id="calc_pocket_volume" />
                           <label
                              className="form-check-label"
                              htmlFor="calc_pocket_volume"
                              title="Calculate pocket volume (slow)"
                           >
                              Calculate pocket volume
                           </label>
                        </div>
                     </div>

                     <div className="options-right">
                        <div className="form-check">
                           <input
                              type="checkbox"
                              className="form-check-input"
                              id="include_alphafold"
                              checked={options.include_alphafold}
                              onChange={(e): void => setOptions({ ...options, include_alphafold: e.target.checked })}
                           />
                           <label
                              className="form-check-label"
                              htmlFor="include_alphafold"
                              title="0/1: Download AF models and use them as candidates and/or align them to query"
                           >
                              Include AlphaFold DB model
                           </label>
                        </div>
                        <div className="form-check">
                           <input
                              type="checkbox"
                              className="form-check-input"
                              id="save_apo"
                              checked={options.save_apo}
                              onChange={(e): void => setOptions({ ...options, save_apo: e.target.checked })}
                           />
                           <label
                              className="form-check-label"
                              htmlFor="save_apo"
                              title="Save the structures of aligned chains with APO states (cif.gz). Disabling this is only recommended in multiple queries if visualizations are not needed (reduces download size). This setting does not affect the search for apo or holo chains or the final result reports."
                           >
                              Save aligned Apo structures
                           </label>
                        </div>
                        <div className="form-check">
                           <input
                              type="checkbox"
                              className="form-check-input"
                              id="save_holo"
                              checked={options.save_holo}
                              onChange={(e): void => setOptions({ ...options, save_holo: e.target.checked })}
                           />
                           <label
                              className="form-check-label"
                              htmlFor="save_holo"
                              title="Save the structures of aligned chains with HOLO states (cif.gz). Disabling this is only recommended in multiple queries if visualizations are not needed (reduces download size). This setting does not affect the search for apo or holo chains or the final result reports."
                           >
                              Save aligned Holo structures
                           </label>
                        </div>
                        <div className="form-check">
                           <input
                              type="checkbox"
                              className="form-check-input"
                              id="save_unobserved"
                              checked={options.save_unobserved}
                              onChange={(e): void => setOptions({ ...options, save_unobserved: e.target.checked })}
                           />
                           <label
                              className="form-check-label"
                              htmlFor="save_unobserved"
                              title="Save the structures of aligned chains with Unobserved states, if the UniProt chain overlap with the query is over zero (cif.gz). Disabling this is only recommended in multiple queries if visualizations are not needed (reduces download size). This setting does not affect the search for apo or holo chains or the final result reports."
                           >
                              Save aligned Unobserved structures
                           </label>
                        </div>
                        <div style={{ height: '1em' }}></div>
                        <div className="form-group option">
                           <label
                              htmlFor="bndgrsds_threshold"
                              title="Percentage of binding residues of the query that have to be present in the candidate SEQRES according to SIFTS UniProt residue mapping, for the candidate to be considered, condition is '>='. min/max = 1/100"
                           >
                              Binding residues threshold:
                           </label>
                           <input
                              type="text"
                              className="form-control"
                              placeholder=""
                              id="bndgrsds_threshold"
                              style={{ width: '7em' }}
                              value={options.bndgrsds_threshold}
                              onChange={(e): void => setOptions({ ...options, bndgrsds_threshold: e.target.value })}
                           />
                           &nbsp;&nbsp;%&nbsp;&nbsp;&nbsp;<span className="opt-description"></span>
                        </div>
                        <div className="form-group option">
                           <label
                              htmlFor="res_threshold"
                              title="highest allowed resolution for result chains, condition is &lt;="
                           >
                              Resolution threshold:
                           </label>
                           <input
                              type="text"
                              className="form-control"
                              placeholder=""
                              id="res_threshold"
                              defaultChecked={false}
                              style={{ width: '7em' }}
                              value={options.res_threshold}
                              onChange={(e): void => setOptions({ ...options, res_threshold: e.target.value })}
                           />
                           &nbsp;&nbsp;Å&nbsp;&nbsp;&nbsp;<span className="opt-description"></span>
                        </div>
                        <div className="form-group option">
                           <label
                              htmlFor="lig_scan_radius"
                              title="binding distance threshold in angstroms (Å) for detecting bonds between protein residues and ligands"
                           >
                              Ligand scanning radius:
                           </label>
                           <input
                              type="text"
                              className="form-control"
                              placeholder=""
                              id="lig_scan_radius"
                              style={{ width: '7em' }}
                              value={options.lig_scan_radius}
                              onChange={(e): void => setOptions({ ...options, lig_scan_radius: e.target.value })}
                           />
                           &nbsp;&nbsp;Å&nbsp;&nbsp;&nbsp;<span className="opt-description"></span>
                        </div>
                     </div>
                  </div>
               </div>

               <br />
               {submitError && <div style={{ color: 'red', marginBottom: '0.5em' }}>{submitError}</div>}
               <button type="submit" className="btn btn-primary">
                  Submit Job
               </button>

               <span style={{ float: 'right', marginTop: '1em', marginRight: '0.7em' }}>
                  <Link
                     to={{
                        pathname: '/my-jobs',
                     }}
                     title="Go to a page with recently executed jobs"
                  >
                     recent jobs
                  </Link>
               </span>
            </div>
         </form>
      </div>
   );
};

export default SearchForApoHoloPairs;
